<template>
  <div class="water-quality-analysis">
    <div class="chart-header">
      <div class="metric-selector">
        <div class="tabs">
          <div 
            v-for="(tab, index) in tabList" 
            :key="tab.key"
            class="tab"
            :class="{ active: activeTab === tab.key }"
            @click="handleTabChange(tab.key, index)"
          >
            {{ tab.title }}
          </div>
        </div>
      </div>
      <div class="current-info">
        <span class="current-value">{{ currentValue }}</span>
        <span class="current-unit">{{ currentMetric.unit }}</span>
      </div>
    </div>
    
    <div class="chart-container">
      <Echart 
        ref="chart" 
        :options="chartOption" 
        class="quality-chart"
      />
    </div>
    
    <div class="threshold-indicators">
      <div class="indicator-item" v-if="threshold.low">
        <span class="indicator-label">{{ $t('下限') }}</span>
        <span class="indicator-value">{{ threshold.low }}</span>
      </div>
      <div class="indicator-item" v-if="threshold.high">
        <span class="indicator-label">{{ $t('上限') }}</span>
        <span class="indicator-value">{{ threshold.high }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Echart from '@/components/Echart/echarts'
import * as dayjs from 'dayjs'
import { getAction } from '@/api/manage'
import { isUndefinedAndNull } from '@/utils/validate'

// Import ECharts modules
import 'echarts/lib/chart/line'
import 'echarts/lib/component/markLine'
import 'echarts/lib/component/markPoint'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'

export default {
  name: 'WaterQualityIndexLineAnalysis',
  components: {
    Echart
  },
  props: {
    title: {
      type: String,
      default() {
        return this.$t('水质指标分析')
      }
    }
  },
  data() {
    return {
      activeTab: 'w01001',
      tabList: [
        { key: 'w01001', title: 'PH', unit: 'PH' },
        { key: 'w01002', title: this.$t('色度'), unit: this.$t('度') },
        { key: 'w01003', title: this.$t('浊度'), unit: 'NTU' },
        { key: 'w10000', title: this.$t('硬度'), unit: 'mg/L' },
        { key: 'w10001', title: this.$t('氧化还原'), unit: 'mV' },
        { key: 'w21022', title: this.$t('氯化物'), unit: 'mg/L' },
        { key: 'w01010', title: this.$t('温度'), unit: '℃' },
        { key: 'w21024', title: this.$t('余氯'), unit: 'mg/L' },
        { key: 'w01014', title: this.$t('电导率'), unit: 'μS/cm' }
      ],
      currentDevice: {
        deviceName: '水厂末梢水水质',
        deviceLocation: '哈尔莫顿镇末梢水'
      },
      chartData: [],
      timeData: [],
      chartOption: {},
      currentValue: 0,
      threshold: {
        low: null,
        high: null
      },
      url: {
        list: '/jeecg-charge/part/partWaterQualityMinuteRecord/analysisByDateList',
        waterQualityDeviceList: "/jeecg-charge/part/partWaterQualityLastRecord/list"
      },
      dataSource: [],
      waterQualityMeterId: '',
      waterQualityMeter: {},
      minuteRecod: []
    }
  },
  computed: {
    currentMetric() {
      return this.tabList.find(tab => tab.key === this.activeTab) || {}
    }
  },
  watch: {
    activeTab: {
      handler() {
        this.queryWaterQualityMinuteRecord()
      },
      immediate: true
    }
  },
  mounted() {
    this.loadMockDeviceData();
        this.loadFallbackData();
    // this.queryWaterQualityDevice()
  },
  methods: {
    
    handleTabChange(tabKey, index) {
      this.activeTab = tabKey
      this.queryWaterQualityMinuteRecord(index)
    },
    
    queryWaterQualityDevice() {
      let params = {};
      getAction(this.url.waterQualityDeviceList, params).then((res) => {
        if (res.success && res.result && res.result.records && res.result.records.length > 0) {
          let dataSource = res.result.records;
          this.dataSource = dataSource;
          this.waterQualityMeterId = this.dataSource[0].meterId;
          this.waterQualityMeter = this.dataSource[0];
          this.currentDevice.deviceName = this.dataSource[0].deviceName;
          this.currentDevice.deviceLocation = this.dataSource[0].deviceLocation;
          this.queryWaterQualityMinuteRecord(0);
        } else {
          // Fallback to mock data if API fails
          console.warn('Failed to load water quality devices, using mock data');
          this.loadMockDeviceData();
          this.loadFallbackData();
        }
      }).catch((error) => {
        console.error('Error loading water quality devices:', error);
        // Fallback to mock data if API fails
        this.loadMockDeviceData();
        this.loadFallbackData();
      });
    },

    queryWaterQualityMinuteRecord(arg = 0) {
      if (this.dataSource.length < 1 && !this.waterQualityMeterId) {
        this.loadFallbackData();
        return;
      }
      
      let date = dayjs(new Date()).format("YYYY-MM-DD");
      var params = {
        deviceId: this.waterQualityMeterId || (this.dataSource[0] && this.dataSource[0].meterId),
        cmd: this.activeTab,
        beiginDate: date
      };
      
      getAction(this.url.list, params).then((res) => {
        if (res.success) {
          let dataSource = res.result;
          this.minuteRecod = dataSource;
          this.processRealData();
        } else {
          this.loadFallbackData();
        }
      }).catch(() => {
        this.loadFallbackData();
      });
    },

    processRealData() {
      let lineData = this.minuteRecod.avgArr || [];
      if (this.minuteRecod.rtdArr && this.minuteRecod.rtdArr.length > 0) {
        lineData = this.minuteRecod.rtdArr;
      }
      
      let xAxisData = [];
      for (let i = 0; i < (this.minuteRecod.dateArr || []).length; i++) {
        xAxisData.push((this.minuteRecod.dateArr[i] || '').substring(11));
      }
      
      this.chartData = lineData;
      this.timeData = xAxisData;
      this.currentValue = lineData[lineData.length - 1] || 0;
      this.setRealThresholds();
      this.updateChart();
    },

    setRealThresholds() {
      if(this.waterQualityMeter && this.waterQualityMeter.remark) {
        try {
          let remark = JSON.parse(this.waterQualityMeter.remark);
          this.threshold.low = remark[this.activeTab + '_down'];
          this.threshold.high = remark[this.activeTab + '_up'];
        } catch(e) {
          this.setFallbackThresholds();
        }
      } else {
        this.setFallbackThresholds();
      }
    },

    setFallbackThresholds() {
      const thresholds = {
        'w01001': { low: 6.5, high: 8.5 },
        'w01002': { low: 0, high: 15 },
        'w01003': { low: 0, high: 1.0 },
        'w10000': { low: 0, high: 450 },
        'w10001': { low: -100, high: 100 },
        'w21022': { low: 0, high: 250 },
        'w01010': { low: 0, high: 35 },
        'w21024': { low: 0.05, high: 0.5 },
        'w01014': { low: 100, high: 1000 }
      }
      this.threshold = thresholds[this.activeTab] || { low: null, high: null }
    },

    loadMockDeviceData() {
      // Mock device data when API fails
      this.dataSource = [{
        deviceName: "水厂末梢水水质",
        deviceLocation: "哈尔莫顿镇末梢水",
        meterId: "230911XF02",
        remark: JSON.stringify({
          w01001_down: 6.5,
          w01001_up: 8.5,
          w01002_down: 0,
          w01002_up: 15,
          w01003_down: 0,
          w01003_up: 1.0,
          w10000_down: 0,
          w10000_up: 450,
          w10001_down: -100,
          w10001_up: 100,
          w21022_down: 0,
          w21022_up: 250,
          w01010_down: 0,
          w01010_up: 35,
          w21024_down: 0.05,
          w21024_up: 0.5,
          w01014_down: 100,
          w01014_up: 1000
        })
      }];
      this.waterQualityMeter = this.dataSource[0];
      this.waterQualityMeterId = this.dataSource[0].meterId;
      this.currentDevice.deviceName = this.dataSource[0].deviceName;
      this.currentDevice.deviceLocation = this.dataSource[0].deviceLocation;
    },

    loadFallbackData() {
      // Fallback to mock data when API is not available
      console.log('Loading fallback mock data...');
      const mockData = this.generateMockData();
      this.chartData = mockData.values;
      this.timeData = mockData.times;
      this.currentValue = mockData.values[mockData.values.length - 1];
      this.setRealThresholds(); // Use real thresholds from mock device
      this.updateChart();
    },

    generateMockData() {
      const values = []
      const times = []
      const baseValue = this.getBaseValue()
      const variation = this.getVariation()
      
      for (let i = 0; i < 12; i++) {
        const timestamp = dayjs().subtract(11 - i, 'hour').format('HH:mm')
        const value = baseValue + (Math.random() - 0.5) * variation
        values.push(parseFloat(value.toFixed(3)))
        times.push(timestamp)
      }
      
      return { values, times }
    },
    
    getBaseValue() {
      const baseValues = {
        'w01001': 7.0,
        'w01002': 5.0,
        'w01003': 0.05,
        'w10000': 200,
        'w10001': 0,
        'w21022': 50,
        'w01010': 25,
        'w21024': 0.1,
        'w01014': 400
      }
      return baseValues[this.activeTab] || 0
    },
    
    getVariation() {
      const variations = {
        'w01001': 0.5,
        'w01002': 2,
        'w01003': 0.02,
        'w10000': 50,
        'w10001': 20,
        'w21022': 10,
        'w01010': 5,
        'w21024': 0.05,
        'w01014': 50
      }
      return variations[this.activeTab] || 1
    },
    
    updateChart() {
      const colors = {
        'w01001': '#00ff88',
        'w01002': '#ff6b35',
        'w01003': '#00d4ff',
        'w10000': '#9c27b0',
        'w10001': '#ffeb3b',
        'w21022': '#f44336',
        'w01010': '#ff9800',
        'w21024': '#2196f3',
        'w01014': '#e91e63'
      }
      
      const lineColor = colors[this.activeTab] || '#00d4ff'
      
      this.chartOption = {
        color: [lineColor],
        title: {
          show: false
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 11, 42, 0.9)',
          borderColor: '#5D6A94',
          borderWidth: 1,
          borderRadius: 8,
          textStyle: {
            color: '#CEE2FF'
          },
          formatter: (params) => {
            const param = params[0]
            return `
              <div style="padding: 8px;">
                <div style="font-size: 12px; color: ${lineColor}; margin-bottom: 4px;">${param.name}</div>
                <div style="font-size: 14px; font-weight: bold;">${this.currentMetric.title}: ${param.value} ${this.currentMetric.unit}</div>
              </div>
            `
          }
        },
        grid: {
          left: '8%',
          right: '8%',
          bottom: '8%',
          top: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.timeData,
          splitLine: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.2)'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 10,
            interval: 1
          }
        },
        yAxis: {
          type: 'value',
          name: this.currentMetric.unit,
          nameTextStyle: {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 10,
            align: 'right',
            padding: [0, 4, 0, 0]
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 10,
            margin: 4
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.1)',
              type: 'dashed'
            }
          }
        },
        series: [{
          name: this.currentMetric.title,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          sampling: 'average',
          itemStyle: {
            color: lineColor
          },
          lineStyle: {
            width: 2,
            shadowColor: lineColor,
            shadowBlur: 8,
            shadowOffsetY: 0
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: lineColor + '30' },
                { offset: 0.5, color: lineColor + '10' },
                { offset: 1, color: lineColor + '02' }
              ]
            }
          },
          data: this.chartData,
          markLine: this.getMarkLine(),
          markPoint: {
            symbol: 'pin',
            symbolSize: 20,
            data: [
              { type: 'max', name: this.$t('最大值') },
              { type: 'min', name: this.$t('最小值') }
            ],
            itemStyle: {
              color: lineColor
            }
          }
        }]
      }
    },
    
    getMarkLine() {
      if (!this.threshold.low && !this.threshold.high) {
        return null
      }
      
      const data = []
      
      if (this.threshold.low !== null) {
        data.push({
          yAxis: this.threshold.low,
          lineStyle: {
            color: '#ff9800',
            type: 'solid',
            width: 1
          },
          label: {
            formatter: this.$t('下限'),
            color: '#ff9800',
            fontSize: 10,
            position: 'start'
          }
        })
      }
      
      if (this.threshold.high !== null) {
        data.push({
          yAxis: this.threshold.high,
          lineStyle: {
            color: '#f44336',
            type: 'solid',
            width: 1
          },
          label: {
            formatter: this.$t('上限'),
            color: '#f44336',
            fontSize: 10,
            position: 'start'
          }
        })
      }
      
      return data.length > 0 ? { data, symbol: 'none' } : null
    }
  }
}
</script>

<style lang="less" scoped>
.water-quality-analysis {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px;
  box-sizing: border-box;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  height: 28px;
}

.metric-selector {
  flex: 1;
}

.tabs {
  display: flex;
  gap: 4px;
  background: rgba(0, 20, 40, 0.5);
  border-radius: 4px;
  padding: 2px;
  height: 24px;
}

.tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 2px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  background: transparent;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab.active {
  background: rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
}

.tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.current-info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
}

.current-value {
  font-family: 'ds-digital-bold';
  font-size: 16px;
  font-weight: bold;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.current-unit {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
}

.chart-container {
  flex: 1;
  position: relative;
  background: rgba(0, 20, 40, 0.3);
  border: 1px solid rgba(0, 150, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
  min-height: 0;
}

.quality-chart {
  width: 100%;
  height: 100%;
}

.threshold-indicators {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  justify-content: center;
}

.indicator-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: rgba(0, 20, 40, 0.5);
  border-radius: 3px;
  border: 1px solid rgba(0, 150, 255, 0.2);
}

.indicator-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.6);
}

.indicator-value {
  font-size: 9px;
  font-weight: bold;
}

.indicator-item:first-child .indicator-value {
  color: #ff9800;
}

.indicator-item:last-child .indicator-value {
  color: #f44336;
}

// Responsive for 40% height
@media (max-height: 400px) {
  .chart-header {
    margin-bottom: 4px;
    height: 24px;
  }
  
  .tabs {
    height: 20px;
  }
  
  .tab {
    font-size: 10px;
    padding: 1px 2px;
  }
  
  .current-value {
    font-size: 14px;
  }
  
  .current-unit {
    font-size: 9px;
  }
  
  .threshold-indicators {
    margin-top: 2px;
    gap: 4px;
  }
  
  .indicator-item {
    padding: 1px 4px;
  }
  
  .indicator-label,
  .indicator-value {
    font-size: 8px;
  }
}
</style>